/**
 * Detect Shopify vs Amazon inventory snapshot CSV channels on org connection objects.
 * Parent `ecommerce_subtype` can be wrong when multiple inventory imports share one connection;
 * import ids follow `..._inventory_shopify_...` / `..._inventory_amazon_...` (UnifiedCsvImport / SnapshotCsvUpload).
 */

/** Structured inventory snapshot ids: `_inventory_amazon_YYYYMMDD`, `_inventory_shopify_YYYYMMDD`, or legacy `_inventory_YYYYMMDD`. */
export const INVENTORY_SNAPSHOT_ID_RE = /_inventory_(?:amazon|shopify|\d{8})(?:_|$)/i;

/**
 * True only for real inventory-snapshot connector ids, not filenames that happen
 * to contain the word "inventory" (e.g. `scenario_a_many_inventory_to_one_sales_...`).
 */
export function isInventorySnapshotConnectionId(connectionId?: string | null): boolean {
  return INVENTORY_SNAPSHOT_ID_RE.test(String(connectionId || ''));
}

export function isCsvLikeConnection(conn: {
  type?: string;
  service?: string;
  id?: string;
  connectionId?: string;
}): boolean {
  const type = String(conn.type || '').toLowerCase();
  const service = String(conn.service || '').toLowerCase();
  const id = String(conn.id || '').toLowerCase();
  const connectionId = String(conn.connectionId || '').toLowerCase();
  return (
    service === 'csv' ||
    type === 'csv' ||
    connectionId.startsWith('csv-import-') ||
    id.startsWith('csv-import-')
  );
}

export function connectionIdOrImportsSuggestShopifyInventory(conn: {
  id?: string;
  csvImports?: Array<{ id?: string }>;
}): boolean {
  const root = String(conn.id || '').toLowerCase();
  if (root.includes('_inventory_shopify_')) return true;
  const imports = conn.csvImports;
  if (!Array.isArray(imports)) return false;
  return imports.some((imp) => String(imp?.id || '').toLowerCase().includes('_inventory_shopify_'));
}

export function connectionIdOrImportsSuggestAmazonInventory(conn: {
  id?: string;
  csvImports?: Array<{ id?: string }>;
}): boolean {
  const root = String(conn.id || '').toLowerCase();
  if (root.includes('_inventory_amazon_')) return true;
  const imports = conn.csvImports;
  if (!Array.isArray(imports)) return false;
  return imports.some((imp) => String(imp?.id || '').toLowerCase().includes('_inventory_amazon_'));
}

function rootOrImportsHaveGenericInventoryMarker(conn: {
  id?: string;
  csvImports?: Array<{ id?: string }>;
}): boolean {
  if (isInventorySnapshotConnectionId(conn.id)) return true;
  const imports = conn.csvImports;
  if (!Array.isArray(imports)) return false;
  return imports.some((imp) => isInventorySnapshotConnectionId(imp?.id));
}

function legacySubtypeInventoryCsv(
  conn: { ecommerce_subtype?: string; id?: string; csvImports?: Array<{ id?: string }> },
  subtype: 'shopify' | 'amazon',
): boolean {
  if (String(conn.ecommerce_subtype || '').toLowerCase() !== subtype) return false;
  return rootOrImportsHaveGenericInventoryMarker(conn);
}

/**
 * Prefer explicit `_inventory_shopify_` / `_inventory_amazon_` in connection or csvImports ids.
 * If only one channel is explicit, do not infer the other channel from parent subtype (avoids wrong parent metadata).
 */
export function connectionHasShopifyInventoryCsv(conn: Record<string, unknown>): boolean {
  if (!isCsvLikeConnection(conn)) return false;
  if (connectionIdOrImportsSuggestShopifyInventory(conn)) return true;
  if (connectionIdOrImportsSuggestAmazonInventory(conn)) return false;
  return legacySubtypeInventoryCsv(conn, 'shopify');
}

export function connectionHasAmazonInventoryCsv(conn: Record<string, unknown>): boolean {
  if (!isCsvLikeConnection(conn)) return false;
  if (connectionIdOrImportsSuggestAmazonInventory(conn)) return true;
  if (connectionIdOrImportsSuggestShopifyInventory(conn)) return false;
  return legacySubtypeInventoryCsv(conn, 'amazon');
}
